/**
 * WP-16: sharing a run, from the page's side.
 *
 * - `shareLiveRun`: in live mode, ask the dashboard Worker to archive the repo's run. The browser's session cookie
 *   goes with the request; the Worker checks it with the accounts Worker, mints the event ticket with it, reads
 *   the stream and archives it. The page never holds a token (WP-23).
 * - `loadSharedRun`: on a share page, fetch the archived run from this Worker. No credential is involved.
 */
import type { PlatformEvent } from "@botallion/contracts";
import { parseRecording } from "./replay.ts";

/** Mirrors the Worker's RunMeta (src/worker/runs.ts). */
export interface SharedRunMeta {
  id: string;
  name: string;
  createdAt: string;
  source: { kind: "coordinator" | "upload"; repo: string | null };
  org?: string;
  repo?: string;
  publishedBy?: string;
  eventCount: number;
  firstTs: string;
  lastTs: string;
  lastSeq: number;
  digest: string;
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

async function body<T>(res: Response): Promise<T> {
  let parsed: { ok?: boolean; result?: T; error?: { message?: string } } = {};
  try {
    parsed = (await res.json()) as typeof parsed;
  } catch {
    // below
  }
  if (!res.ok || !parsed.ok || parsed.result === undefined) throw new Error(parsed.error?.message ?? `HTTP ${res.status}`);
  return parsed.result;
}

/** The run id in a share page's path, `/r/<id>`, or null. */
export function runIdFromPath(pathname: string): string | null {
  const m = /^\/r\/([A-Za-z0-9_-]{22})\/?$/.exec(pathname);
  return m ? m[1]! : null;
}

export async function loadSharedRun(id: string, doFetch: Fetch = (i, init) => fetch(i, init)): Promise<{ meta: SharedRunMeta; events: PlatformEvent[] }> {
  const res = await doFetch(`/api/runs/${encodeURIComponent(id)}`, { credentials: "omit" });
  if (res.status === 404) throw new Error("There is no run at this link. It may have been withdrawn.");
  const run = await body<{ meta: SharedRunMeta; events: unknown[] }>(res);
  return { meta: run.meta, events: parseRecording(run.events) };
}

export async function shareLiveRun(opts: { org: string; repo: string; name: string; fetch?: Fetch }): Promise<{ url: string; run: SharedRunMeta }> {
  const doFetch = opts.fetch ?? ((i: string, init?: RequestInit) => fetch(i, init));
  const res = await doFetch("/api/runs/from-coordinator", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ org: opts.org, repo: opts.repo, name: opts.name }),
  });
  if (res.status === 401) throw new Error("Your session has ended. Sign in again.");
  return body<{ url: string; run: SharedRunMeta }>(res);
}
